import {
  CSSProperties,
  FC,
  PointerEvent as ReactPointerEvent,
  UIEvent as ReactUIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PrismicNextLink } from '@prismicio/next';
import { isFilled } from '@prismicio/client';
import type { Content } from '@prismicio/client';
import classNames from 'classnames';

import { PrismicImageWithAlt } from '@/components/PrismicImageWithAlt';
import { resolveLink } from '@/utils';

import styles from './ContentMarqueeCarousel.module.scss';

type ContentMarqueeCarouselSliceDefaultItem =
  Content.ContentMarqueeCarouselSliceDefaultItem;

export const LOOP_MIN_SLIDES = 5;
const DRAG_THRESHOLD_PX = 6;
const INTERACTIVE_TARGET_SELECTOR =
  'a,button,input,textarea,select,summary,[role="button"]';

export type AutoplayMode = 'disabled' | 'marquee' | 'slow' | 'fast';

export function getSliderMotion(
  mode: AutoplayMode | null | undefined,
  disableOnInteraction: boolean,
): {
  activeAutoplay: boolean;
  /** Marquee mode: continuous CSS scroll (no Swiper / no loop jumps). */
  continuousMarquee: boolean;
  autoplay:
    | {
        delay: number;
        disableOnInteraction: boolean;
        pauseOnMouseEnter: boolean;
        waitForTransition: boolean;
      }
    | undefined;
  freeMode: boolean;
  loop: boolean;
  speed: number;
} {
  const m = (mode ?? 'disabled') as AutoplayMode;
  // Avoid autoplay stuck waiting for `transitionend` (freeMode + slidesPerView auto).
  const autoplayBase = {
    disableOnInteraction,
    pauseOnMouseEnter: false,
    waitForTransition: false,
  };

  if (m === 'disabled') {
    return {
      activeAutoplay: false,
      continuousMarquee: false,
      autoplay: undefined,
      freeMode: true,
      loop: false,
      speed: 600,
    };
  }

  if (m === 'marquee') {
    return {
      activeAutoplay: true,
      continuousMarquee: true,
      autoplay: { ...autoplayBase, delay: 0 },
      freeMode: true,
      loop: true,
      speed: 5500,
    };
  }

  if (m === 'slow') {
    return {
      activeAutoplay: true,
      continuousMarquee: false,
      autoplay: { ...autoplayBase, delay: 3500 },
      freeMode: true,
      loop: true,
      speed: 2200,
    };
  }

  return {
    activeAutoplay: true,
    continuousMarquee: false,
    autoplay: { ...autoplayBase, delay: 1200 },
    freeMode: true,
    loop: true,
    speed: 900,
  };
}

export function itemHasVisibleContent(
  item: ContentMarqueeCarouselSliceDefaultItem,
) {
  const hasImage = isFilled.image(item?.image);
  const hasLabel = Boolean(item?.label?.trim());
  return hasImage || hasLabel;
}

type MarqueeSlideProps = {
  item: ContentMarqueeCarouselSliceDefaultItem;
  /** When true, image fills the CMS slot height; when false, natural image height (no CMS heights). */
  constrainImageToSlot?: boolean;
};

const MarqueeSlide: FC<MarqueeSlideProps> = ({
  item,
  constrainImageToSlot = true,
}) => {
  const resolved = resolveLink(item?.internal_link, item?.external_link, {
    externalNewTab: true,
  });

  const opensNewTab = resolved.kind === 'external' || resolved.kind === 'media';
  const label = item?.label?.trim();
  const ariaBase = label || 'Marquee item';
  const ariaLabel = opensNewTab ? `${ariaBase} (opens in a new tab)` : ariaBase;

  const hasImage = isFilled.image(item.image);
  const hasLabel = Boolean(label);

  const inner = (
    <>
      {hasImage ? (
        <figure className={styles.content_marquee__figure}>
          <PrismicImageWithAlt
            className={styles.content_marquee__img_fill}
            defaultAlt={label ? `${label} image` : 'Marquee image'}
            fallbackAlt=""
            field={item.image}
            quality={100}
            style={
              constrainImageToSlot
                ? { height: '100%', width: 'auto' }
                : { height: 'auto', width: 'auto' }
            }
          />
        </figure>
      ) : null}
      {hasLabel ? (
        <span className={styles.content_marquee__label}>{label}</span>
      ) : null}
    </>
  );

  const itemClass = classNames(styles.content_marquee__item, {
    [styles.content_marquee__item_with_caption]: hasLabel,
    [styles.content_marquee__item_text_only]: hasLabel && !hasImage,
    [styles.content_marquee__item_align_center]: !constrainImageToSlot,
  });

  if (resolved.kind === 'empty') {
    return <div className={itemClass}>{inner}</div>;
  }

  return (
    <PrismicNextLink
      {...(resolved.props as any)}
      aria-label={ariaLabel}
      className={itemClass}
    >
      {inner}
    </PrismicNextLink>
  );
};

export type ContentMarqueeCarouselContinuousProps = {
  constrainImageToSlot: boolean;
  disableAutoplayOnInteraction?: boolean;
  items: ContentMarqueeCarouselSliceDefaultItem[];
  trackStyle?: CSSProperties;
};

function slideKeyChunk(prefix: string, index: number, label: string) {
  return `${prefix}-${index}-${label ?? ''}`;
}

const ContentMarqueeCarouselContinuous: FC<ContentMarqueeCarouselContinuousProps> =
  ({
    constrainImageToSlot,
    disableAutoplayOnInteraction,
    items,
    trackStyle,
  }) => {
    const [isAutoplayStopped, setIsAutoplayStopped] = useState(false);
    const [isInteractionPaused, setIsInteractionPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const moverRef = useRef<HTMLDivElement | null>(null);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragRef = useRef<{
      pointerId: number;
      startX: number;
      startScrollLeft: number;
      didDrag: boolean;
    } | null>(null);

    const clearResumeTimer = useCallback(() => {
      if (!resumeTimerRef.current) {
        return;
      }
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }, []);

    const scheduleResume = useCallback(
      (delayMs = 900) => {
        clearResumeTimer();
        resumeTimerRef.current = setTimeout(() => {
          setIsInteractionPaused(false);
          resumeTimerRef.current = null;
        }, delayMs);
      },
      [clearResumeTimer],
    );

    const stopAutoplayOnInteraction = useCallback(() => {
      if (disableAutoplayOnInteraction) {
        if (!isAutoplayStopped) {
          setIsAutoplayStopped(true);
        }
        return;
      }

      setIsInteractionPaused(true);
      scheduleResume();
    }, [disableAutoplayOnInteraction, isAutoplayStopped, scheduleResume]);

    const onViewportPointerDown = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        if (disableAutoplayOnInteraction) {
          if (!isAutoplayStopped) {
            setIsAutoplayStopped(true);
          }
        } else {
          clearResumeTimer();
          setIsInteractionPaused(true);
        }

        const viewport = viewportRef.current;
        if (!viewport) {
          return;
        }

        const targetElement = event.target as HTMLElement | null;
        if (targetElement?.closest(INTERACTIVE_TARGET_SELECTOR)) {
          return;
        }

        dragRef.current = {
          didDrag: false,
          pointerId: event.pointerId,
          startScrollLeft: viewport.scrollLeft,
          startX: event.clientX,
        };
      },
      [clearResumeTimer, disableAutoplayOnInteraction, isAutoplayStopped],
    );

    const normalizeViewportScroll = useCallback((nextScrollLeft: number) => {
      const viewport = viewportRef.current;
      const mover = moverRef.current;
      if (!viewport || !mover) {
        return;
      }

      const loopWidth = mover.scrollWidth / 2;
      if (!Number.isFinite(loopWidth) || loopWidth <= 0) {
        viewport.scrollLeft = Math.max(0, nextScrollLeft);
        return;
      }

      let normalized = nextScrollLeft % loopWidth;
      if (normalized < 0) {
        normalized += loopWidth;
      }
      viewport.scrollLeft = normalized;
    }, []);

    const onViewportPointerMove = useCallback(
      (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!(isAutoplayStopped || isInteractionPaused) || !dragRef.current) {
          return;
        }

        const viewport = viewportRef.current;
        if (!viewport) {
          return;
        }

        const deltaX = event.clientX - dragRef.current.startX;
        const isDragGesture =
          dragRef.current.didDrag || Math.abs(deltaX) >= DRAG_THRESHOLD_PX;

        if (!isDragGesture) {
          return;
        }

        if (!dragRef.current.didDrag) {
          dragRef.current.didDrag = true;
          setIsDragging(true);
          viewport.setPointerCapture(event.pointerId);
        }

        event.preventDefault();
        normalizeViewportScroll(dragRef.current.startScrollLeft - deltaX);
      },
      [isAutoplayStopped, isInteractionPaused, normalizeViewportScroll],
    );

    const onViewportScroll = useCallback(
      (event: ReactUIEvent<HTMLDivElement>) => {
        if (!(isAutoplayStopped || isInteractionPaused) || isDragging) {
          return;
        }
        normalizeViewportScroll(event.currentTarget.scrollLeft);
      },
      [
        isAutoplayStopped,
        isInteractionPaused,
        isDragging,
        normalizeViewportScroll,
      ],
    );

    const endViewportDrag = useCallback(() => {
      const viewport = viewportRef.current;
      const dragState = dragRef.current;
      if (
        viewport &&
        dragState &&
        viewport.hasPointerCapture(dragState.pointerId)
      ) {
        viewport.releasePointerCapture(dragState.pointerId);
      }
      dragRef.current = null;
      setIsDragging(false);
      if (!disableAutoplayOnInteraction) {
        scheduleResume();
      }
    }, [disableAutoplayOnInteraction, scheduleResume]);

    useEffect(() => {
      if (!isAutoplayStopped) {
        return;
      }
      // Reset to stable origin so the first item is not clipped after stopping autoplay.
      normalizeViewportScroll(0);
    }, [isAutoplayStopped, normalizeViewportScroll]);

    useEffect(() => () => clearResumeTimer(), [clearResumeTimer]);

    const isMarqueePaused = isAutoplayStopped || isInteractionPaused;

    const chunkClass = classNames(styles.content_marquee__marquee_chunk, {
      [styles.content_marquee__marquee_chunk_align_center]:
        !constrainImageToSlot,
    });

    return (
      <div
        className={classNames(
          styles.slider__container,
          styles.content_marquee__track,
          styles.content_marquee__marquee_wrap,
        )}
        onFocusCapture={stopAutoplayOnInteraction}
        onKeyDownCapture={stopAutoplayOnInteraction}
        onPointerDownCapture={stopAutoplayOnInteraction}
        onWheelCapture={stopAutoplayOnInteraction}
        style={trackStyle}
      >
        <div
          className={classNames(styles.content_marquee__marquee_viewport, {
            [styles.content_marquee__marquee_viewport_paused]: isMarqueePaused,
            [styles.content_marquee__marquee_viewport_dragging]: isDragging,
          })}
          onPointerCancel={endViewportDrag}
          onPointerDown={onViewportPointerDown}
          onPointerLeave={endViewportDrag}
          onPointerMove={onViewportPointerMove}
          onPointerUp={endViewportDrag}
          onScroll={onViewportScroll}
          ref={viewportRef}
        >
          <div
            className={classNames(styles.content_marquee__marquee_mover, {
              [styles.content_marquee__marquee_mover_paused]: isMarqueePaused,
            })}
            ref={moverRef}
          >
            <div className={chunkClass}>
              {items.map((item, index) => (
                <MarqueeSlide
                  constrainImageToSlot={constrainImageToSlot}
                  item={item}
                  key={slideKeyChunk('cmc-m1', index, item?.label ?? '')}
                />
              ))}
            </div>
            <div
              aria-hidden
              className={chunkClass}
              // Visual clone only: avoids duplicate focus and screen-reader output (`inert` + aria-hidden).
              inert
            >
              {items.map((item, index) => (
                <MarqueeSlide
                  constrainImageToSlot={constrainImageToSlot}
                  item={item}
                  key={slideKeyChunk('cmc-m2', index, item?.label ?? '')}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

export { MarqueeSlide, ContentMarqueeCarouselContinuous };
